/**
 * Build the outcome a close should write, preserving what the end-cycle modal
 * does not own.
 *
 * A PATCH replaces the whole outcome sub-object, so every field omitted here is
 * destroyed. That became load-bearing when recording a result stopped closing
 * the cycle: a cycle can now reach the end-cycle modal with an ending already
 * recorded, and `RecordBetaResultModal` stores the raw beta hCG value as the
 * first line of `notes` because no backend field exists for it. Replacing
 * `notes` therefore destroyed the only record of the value, and resetting
 * `dateRecorded` to the close date destroyed the date the result came in — which
 * is what the cycle card and the past-cycle screen display.
 *
 * Extracted from the modal so the rule is testable without rendering it.
 */

import type { CycleOutcome } from "@src/packages/cycles/hooks/useCycles";
import { mergeOutcomeNotes } from "@src/packages/cycles/presenters/outcomeNotes";

export type EndCycleOutcomeType =
  "pregnancy" | "no_pregnancy" | "miscarriage" | "cancelled" | "other";

/**
 * The parts of a recorded ending this merge actually reads.
 *
 * Derived from the API type with `Pick`, so it cannot drift from the schema, but
 * narrow enough that a caller — or a test — can supply it without fabricating a
 * whole outcome record.
 */
export type RecordedEnding = Pick<
  CycleOutcome,
  | "type"
  | "notes"
  | "dateRecorded"
  | "pregnancyOutcome"
  | "miscarriageOutcome"
  | "biochemicalPregnancyObserved"
  | "weekCount"
  | "liveBirthCount"
>;

export type EndCycleOutcomeDraft = {
  /** Null for a preservation or donation cycle that simply finished. */
  type: EndCycleOutcomeType | null;
  cancellationReason: string | null;
  dateRecorded: string;
  notes: string | null;
  pregnancyOutcome: RecordedEnding["pregnancyOutcome"] | null;
  miscarriageOutcome: RecordedEnding["miscarriageOutcome"] | null;
  biochemicalPregnancyObserved: boolean | null;
  weekCount: number | null;
  liveBirthCount: number | null;
};

export function buildEndCycleOutcome({
  outcomeType,
  cancellationReason,
  addedNote,
  today,
  recorded,
}: {
  outcomeType: EndCycleOutcomeType | null;
  cancellationReason: string | null;
  /** The note typed into the modal, if any. */
  addedNote: string;
  today: string;
  /** The ending already recorded on this cycle, if any. */
  recorded: RecordedEnding | null;
}): EndCycleOutcomeDraft {
  /**
   * Detail that describes one specific ending survives only while that ending
   * stands. Changing the ending clears it — a delivery outcome must never
   * survive onto a loss.
   */
  const keepsRecordedDetail = !!recorded && recorded.type === outcomeType;

  /**
   * Except for what she measured rather than concluded.
   *
   * `pregnancyOutcome`, `miscarriageOutcome` and `liveBirthCount` each name an
   * ending, so a changed ending has to clear them. A week count and an observed
   * biochemical pregnancy name nothing: they are things she reported, and they
   * stay true whatever the cycle is later filed as.
   *
   * They were being cleared with the rest, and the ending that reaches this most
   * often is `other` — the only choice beside the recorded result once one
   * exists. So a patient who had said "I'm seven weeks" and then closed the
   * cycle as "Other" had her seven weeks deleted, with nothing on screen saying
   * so, and no surface that would take them again.
   *
   * What keeps a surviving week count from being *shown* against an ending that
   * is not a pregnancy is `resolvePostProcedureOutcomeClinicalState` refusing to
   * read `other` as one — `summarisePregnancyStatus` does not check `type`, so it
   * still answers "continuing" for this record. That is the whole safety net. If
   * the `other` branch of the resolver is ever reordered or removed, guard it
   * there before touching this.
   */
  const keepsReportedMeasurements = !!recorded;

  const notes = mergeOutcomeNotes({
    existing: recorded?.notes,
    added: addedNote,
  });

  return {
    type: outcomeType,
    cancellationReason: outcomeType === "cancelled" ? cancellationReason : null,
    // Only a changed ending is newly recorded today.
    dateRecorded: keepsRecordedDetail
      ? (recorded?.dateRecorded ?? today)
      : today,
    notes,
    pregnancyOutcome: keepsRecordedDetail
      ? (recorded?.pregnancyOutcome ?? null)
      : null,
    miscarriageOutcome: keepsRecordedDetail
      ? (recorded?.miscarriageOutcome ?? null)
      : null,
    biochemicalPregnancyObserved: keepsReportedMeasurements
      ? (recorded?.biochemicalPregnancyObserved ?? null)
      : null,
    weekCount: keepsReportedMeasurements ? (recorded?.weekCount ?? null) : null,
    liveBirthCount: keepsRecordedDetail
      ? (recorded?.liveBirthCount ?? null)
      : null,
  };
}
